import type { ApiError } from '@lp/contracts';
import { isCompany, useAuth } from './auth';
import { useLocale } from './i18n';

const BASE = '/api/v1';

/** an error answered by the API, in its one shape */
export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiError,
  ) {
    super(body.messageEn);
  }
}

/** the message in the chosen language */
export function errorText(e: unknown): string {
  if (e instanceof ApiFailure) return useLocale.getState().locale === 'km' ? e.body.messageKm : e.body.messageEn;
  return useLocale.getState().locale === 'km' ? 'មិនអាចភ្ជាប់ទៅម៉ាស៊ីនមេបានទេ' : 'Cannot reach the server';
}

let refreshing: Promise<boolean> | null = null;

/** one refresh at a time, however many requests found the access token expired */
function refresh(): Promise<boolean> {
  refreshing ??= (async () => {
    const s = useAuth.getState().session;
    if (!s) return false;
    const r = await fetch(`${BASE}${isCompany(s.user) ? '/company/auth/refresh' : '/auth/refresh'}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refreshToken: s.refreshToken }) });
    if (!r.ok) {
      useAuth.getState().clear();
      return false;
    }
    useAuth.getState().signedIn(await r.json());
    return true;
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

async function call<T>(method: string, path: string, body?: unknown, retry = true): Promise<T> {
  const token = useAuth.getState().session?.accessToken;
  const r = await fetch(`${BASE}${path}`, {
    method,
    headers: { ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  // the access token lasts 15 minutes: renew it once, quietly, and ask again
  if (r.status === 401 && retry && token && !path.startsWith('/auth/login') && !path.startsWith('/company/auth/login') && (await refresh())) return call<T>(method, path, body, false);
  if (r.status === 204) return undefined as T;
  const data = await r.json().catch(() => null);
  if (!r.ok) throw new ApiFailure(r.status, (data as ApiError) ?? { code: 'INTERNAL', messageEn: 'Something went wrong', messageKm: 'មានបញ្ហាអ្វីមួយ' });
  return data as T;
}

export const get = <T>(path: string) => call<T>('GET', path);
export const post = <T>(path: string, body?: unknown) => call<T>('POST', path, body ?? {});
export const put = <T>(path: string, body?: unknown) => call<T>('PUT', path, body ?? {});
export const patch = <T>(path: string, body?: unknown) => call<T>('PATCH', path, body ?? {});
export const del = <T>(path: string) => call<T>('DELETE', path);

/** the field problems of a VALIDATION error, one line each ("games.0.draws.1.time: HH:MM") */
export function fieldProblems(e: unknown): string[] {
  if (!(e instanceof ApiFailure) || e.body.code !== 'VALIDATION') return [];
  const fields = (e.body.details as { fields?: { path: string; message: string }[] } | undefined)?.fields ?? [];
  return fields.map((f) => (f.path ? `${f.path}: ${f.message}` : f.message));
}
